import { useState } from "react";
import { Link, useParams } from "react-router";
import {
  ArrowLeft,
  ArrowRight,
  Activity,
  Wallet,
  Users,
  Calendar,
  Map as MapIcon,
  HeartHandshake,
  TrendingUp,
  Settings as SettingsIcon,
  Pencil,
  Trash2,
  PlayCircle,
  PlusCircle,
  UserPlus,
  Share2,
} from "lucide-react";
import { useAppContext } from "@/providers";
import { useInitiativeDetail } from "@/hooks/use-initiative-detail";
import { useWalletBalance } from "@/hooks/use-wallet-balance";
import { useJuthoorInitiativeStats } from "@/hooks/use-juthoor-initiative-stats";
import { parseApiFloat } from "@/lib/utils/format";
import type { JuthoorInitiative } from "@/types/juthoor";
import { InitiativeDetailsInfo } from "@/components/initiatives/initiative-details-info";
import { FundInitiativeModal } from "@/components/initiatives/fund-initiative-modal";
import { InviteParticipantModal } from "@/components/initiatives/invite-participant-modal";
import { ReactivateInitiativeModal } from "@/components/initiatives/reactivate-initiative-modal";
import { EditInitiativeModal } from "@/components/initiatives/edit-initiative-modal";
import { DeleteInitiativeDialog } from "@/components/initiatives/delete-initiative-dialog";
import { TypeAnalyticsSection } from "@/components/initiatives/type-analytics-section";
import { WalkAnalyticsPanel } from "@/components/initiatives/walk-analytics-panel";
import { InterestAnalyticsSection } from "@/components/initiatives/interest-analytics-section";
import { InitiativeEnablersSection } from "@/components/initiatives/initiative-enablers-section";
import { InitiativeLocationMap } from "@/components/initiatives/initiative-location-map";
import { JuthoorHeroActions } from "@/components/initiatives/juthoor-hero-actions";
import {
  JuthoorStatusBanners,
  JuthoorOverviewKpiStrip,
  JuthoorAnalyticsPanel,
  JuthoorLocationSection,
  JuthoorParticipantsSection,
  JuthoorSettingsInfo,
} from "@/components/initiatives/juthoor-detail-sections";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { ErrorMessage } from "@/components/ui/error-message";
import { PageSection } from "@/components/ui/page-section";
import { PageJumpNav, type PageJumpLink } from "@/components/ui/page-jump-nav";
import { HeroAction } from "@/components/ui/hero-action";
import { t } from "@/lib/i18n/messages";
import { getInitiativeStatus, getInitiativeType } from "@/lib/utils/initiative-helpers";
import { formatNumber } from "@/lib/utils/format";
import { cn } from "@/lib/utils";
import type { Initiative } from "@/types/initiative";

type ActiveModal = "fund" | "invite" | "reactivate" | "edit" | "delete" | null;

const SECTION_LINKS: PageJumpLink[] = [
  { id: "settings", num: "01", ar: "التفاصيل", en: "Details" },
  { id: "overview", num: "02", ar: "نظرة", en: "Overview" },
  { id: "timeline", num: "03", ar: "السلاسل", en: "Timeline" },
  { id: "location", num: "04", ar: "الموقع", en: "Location" },
  { id: "enablers", num: "05", ar: "الممكِّنون", en: "Enablers" },
  { id: "participants", num: "06", ar: "المشاركون", en: "Participants" },
];

export default function InitiativeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { locale } = useAppContext();
  const isRTL = locale === "ar";

  const { initiative, loading, error, refetch } = useInitiativeDetail(id ?? "");
  const { data: walletData } = useWalletBalance();
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const isJuthoorType = (initiative as { type?: string } | null)?.type === "juthoor_initiative";
  const { data: juthoorStats, loading: juthoorStatsLoading } = useJuthoorInitiativeStats(
    id ?? "",
    isJuthoorType,
  );

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-4 md:p-8 flex items-center justify-center min-h-[50vh]">
        <LoadingSpinner size="lg" label={t(locale, "loadingData")} />
      </div>
    );
  }

  if (error || !initiative) {
    return (
      <div className="max-w-7xl mx-auto p-4 md:p-8">
        <ErrorMessage
          title={t(locale, "error")}
          message={error || t(locale, "noData")}
          onRetry={refetch}
          retryLabel={t(locale, "retry")}
        />
      </div>
    );
  }

  const handleModalSuccess = () => {
    setActiveModal(null);
    refetch();
  };

  const isWalk = initiative.type === "walk_initiative";
  const isJuthoor = initiative.type === "juthoor_initiative";
  const juthoorInitiative = isJuthoor ? (initiative as unknown as JuthoorInitiative) : null;
  const hasLocation =
    isJuthoor ||
    (isWalk && (initiative.locations?.length ?? 0) + (initiative.paths?.length ?? 0) > 0);
  const links = SECTION_LINKS.filter(
    (l) => l.id !== "location" || hasLocation,
  );
  // Every section icon on this page follows the initiative's own type color — no other accent colors.
  const sectionTone = isWalk ? "blue" : isJuthoor ? "green" : "primary";

  return (
    <>
      {/* ─── Hero ────────────────────────────────────────────────────── */}
      <DetailHero
        initiative={initiative}
        isRTL={isRTL}
        locale={locale}
        onEdit={() => setActiveModal("edit")}
        onFund={() => setActiveModal("fund")}
        onInvite={() => setActiveModal("invite")}
        onReactivate={() => setActiveModal("reactivate")}
        onDelete={() => setActiveModal("delete")}
        showGenericActions={!isJuthoor}
        extraActions={
          juthoorInitiative && (
            <JuthoorHeroActions
              initiative={juthoorInitiative}
              locale={locale}
              stats={juthoorStats}
              onRefetch={refetch}
            />
          )
        }
      />

      {/* ─── Juthoor status banners (rejection reason / budget required) ─ */}
      {juthoorInitiative && <JuthoorStatusBanners initiative={juthoorInitiative} locale={locale} />}

      {/* ─── Sticky in-page jump nav ─────────────────────────────────── */}
      <PageJumpNav isRTL={isRTL} links={links} />

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-12 space-y-12 md:space-y-16">
        {/* ── 01 · SETTINGS & DETAILS ──────────────────────────────── */}
        <PageSection
          id="settings"
          num="01"
          icon={SettingsIcon}
          title={isRTL ? "التفاصيل والإعدادات" : "Settings & details"}
          subtitle={
            isRTL
              ? "كل ما تحتاج معرفته عن هذه المبادرة."
              : "Everything you need to know about this initiative."
          }
          tone={sectionTone}
        >
          {juthoorInitiative ? (
            <JuthoorSettingsInfo initiative={juthoorInitiative} stats={juthoorStats} locale={locale} />
          ) : (
            <InitiativeDetailsInfo initiative={initiative} locale={locale} />
          )}
        </PageSection>

        {/* ── 02 · OVERVIEW (at a glance) ─────────────────────────── */}
        <PageSection
          id="overview"
          num="02"
          icon={Activity}
          title={isRTL ? "نظرة عامة" : "At a glance"}
          subtitle={
            isRTL
              ? "أبرز الأرقام لهذه المبادرة في لمحة سريعة."
              : "Headline numbers for this initiative at a glance."
          }
          tone={sectionTone}
        >
          {juthoorInitiative ? (
            <JuthoorOverviewKpiStrip initiative={juthoorInitiative} stats={juthoorStats} locale={locale} />
          ) : (
            <OverviewKpiStrip initiative={initiative} isRTL={isRTL} locale={locale} />
          )}
        </PageSection>

        {/* ── 03 · TIMELINE / ANALYTICS ───────────────────────────── */}
        <PageSection
          id="timeline"
          num="03"
          icon={TrendingUp}
          title={isRTL ? "السلاسل الزمنية" : "Timeline & analytics"}
          subtitle={
            isRTL
              ? "أداء المبادرة على مرّ الفترة."
              : "How the initiative performs over time."
          }
          tone={sectionTone}
        >
          {isWalk ? (
            <WalkAnalyticsPanel initiativeId={id ?? ""} />
          ) : isJuthoor ? (
            <JuthoorAnalyticsPanel stats={juthoorStats} loading={juthoorStatsLoading} locale={locale} />
          ) : (
            <TypeAnalyticsSection
              initiativeId={id ?? ""}
              initiativeType={initiative.type}
              locale={locale}
            />
          )}
        </PageSection>

        {/* ── 04 · LOCATION (walk / juthoor only) ─────────────────── */}
        {hasLocation && (
          <PageSection
            id="location"
            num="04"
            icon={MapIcon}
            title={isRTL ? "الموقع" : "Location"}
            subtitle={
              isJuthoor
                ? (isRTL ? "أرض الزراعة المخصصة لهذه المبادرة." : "The planting land assigned to this initiative.")
                : (isRTL ? "نقاط البداية والمسارات على الخريطة." : "Starting points and paths on the map.")
            }
            tone={sectionTone}
          >
            {juthoorInitiative ? (
              <JuthoorLocationSection initiative={juthoorInitiative} locale={locale} />
            ) : (
              <InitiativeLocationMap
                locations={initiative.locations ?? []}
                paths={initiative.paths ?? []}
                walkingAreaType={initiative.type_details?.walking_area_type}
                locale={locale}
              />
            )}
          </PageSection>
        )}

        {/* ── 05 · ENABLERS ──────────────────────────────────────── */}
        <PageSection
          id="enablers"
          num="05"
          icon={HeartHandshake}
          title={isRTL ? "الممكِّنون" : "Enablers"}
          subtitle={
            isRTL
              ? "الشركاء الذين يدعمون هذه المبادرة."
              : "Partners powering this initiative."
          }
          tone={sectionTone}
        >
          <InitiativeEnablersSection
            initiativeId={id ?? ""}
            locale={locale}
            canManage={
              initiative.status === "active" ||
              initiative.status === "published"
            }
          />
        </PageSection>

        {/* ── 06 · PARTICIPANTS & INTEREST ───────────────────────── */}
        <PageSection
          id="participants"
          num="06"
          icon={Users}
          title={
            isJuthoor
              ? (isRTL ? "المشاركون" : "Participants")
              : (isRTL ? "المشاركون والاهتمام" : "Participants & interest")
          }
          subtitle={
            isJuthoor
              ? (isRTL ? "من حجز وزرع وكافأ من هذه المبادرة." : "Who reserved, planted, and earned rewards.")
              : (isRTL ? "من شارك ومن أبدى اهتماماً." : "Who joined and who showed interest.")
          }
          tone={sectionTone}
        >
          {isJuthoor ? (
            <JuthoorParticipantsSection initiativeId={id ?? ""} locale={locale} />
          ) : (
            <InterestAnalyticsSection initiativeId={id ?? ""} locale={locale} />
          )}
        </PageSection>

      </div>

      {activeModal === "fund" && (
        <FundInitiativeModal
          initiative={initiative}
          walletBalance={parseApiFloat(walletData?.n_c_balance ?? "0")}
          locale={locale}
          onSuccess={handleModalSuccess}
          onClose={() => setActiveModal(null)}
        />
      )}
      {activeModal === "invite" && (
        <InviteParticipantModal
          initiativeId={id ?? ""}
          locale={locale}
          onSuccess={handleModalSuccess}
          onClose={() => setActiveModal(null)}
        />
      )}
      {activeModal === "reactivate" && (
        <ReactivateInitiativeModal
          initiative={initiative}
          locale={locale}
          onSuccess={handleModalSuccess}
          onClose={() => setActiveModal(null)}
        />
      )}
      {activeModal === "edit" && (
        <EditInitiativeModal
          initiative={initiative}
          onClose={() => setActiveModal(null)}
          onSuccess={() => {
            setActiveModal(null);
            refetch();
          }}
        />
      )}
      {activeModal === "delete" && (
        <DeleteInitiativeDialog
          initiative={initiative}
          onClose={() => setActiveModal(null)}
        />
      )}
    </>
  );
}

// ─── Hero ────────────────────────────────────────────────────────────────

interface DetailHeroProps {
  initiative: Initiative;
  isRTL: boolean;
  locale: "ar" | "en";
  onEdit: () => void;
  onFund: () => void;
  onInvite: () => void;
  onReactivate: () => void;
  onDelete: () => void;
  /** Whether to show the generic Edit/Fund/Invite/Reactivate/Delete actions — types with their own action set (e.g. Juthoor) disable these. */
  showGenericActions?: boolean;
  /** Type-specific action buttons rendered alongside the generic ones (e.g. Juthoor's Set Budget / Submit for Approval). */
  extraActions?: React.ReactNode;
}

function DetailHero({
  initiative,
  isRTL,
  locale,
  onEdit,
  onFund,
  onInvite,
  onReactivate,
  onDelete,
  showGenericActions = true,
  extraActions,
}: DetailHeroProps) {
  const title =
    locale === "ar"
      ? initiative.title_ar || initiative.title
      : initiative.title_en || initiative.title;
  const description =
    locale === "ar"
      ? initiative.description_ar || initiative.description
      : initiative.description_en || initiative.description;

  const gradient = pillarGradient(initiative.type);
  const canEdit =
    initiative.status !== "completed" &&
    initiative.status !== "cancelled" &&
    initiative.status !== "expired";
  const canDelete =
    initiative.status === "draft" || initiative.status === "funded";
  const canFund = initiative.status === "draft";
  const canInvite =
    initiative.status === "active" || initiative.status === "published";
  const canReactivate =
    initiative.status === "inactive" || initiative.status === "expired";

  return (
    <section
      className={cn(
        "relative overflow-hidden text-white",
        "bg-gradient-to-br",
        gradient,
      )}
    >
      <span
        aria-hidden
        className="absolute -top-24 -end-24 w-72 h-72 rounded-full bg-white/10 pointer-events-none"
      />
      <span
        aria-hidden
        className="absolute -bottom-24 -start-12 w-56 h-56 rounded-full bg-white/10 pointer-events-none"
      />
      <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12 space-y-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <BackLink locale={locale} variant="hero" />
          <div className="flex flex-wrap gap-2">
            {showGenericActions && canEdit && (
              <HeroAction icon={Pencil} label={t(locale, "editInitiative")} onClick={onEdit} />
            )}
            {showGenericActions && canFund && (
              <HeroAction
                icon={PlusCircle}
                label={t(locale, "fundInitiative")}
                onClick={onFund}
                primary
              />
            )}
            {showGenericActions && canInvite && (
              <HeroAction
                icon={UserPlus}
                label={t(locale, "inviteParticipant")}
                onClick={onInvite}
                primary
              />
            )}
            {showGenericActions && canReactivate && (
              <HeroAction
                icon={PlayCircle}
                label={t(locale, "reactivateInitiative")}
                onClick={onReactivate}
              />
            )}
            {extraActions}
            <HeroAction icon={Share2} label={t(locale, "shareInitiative")} />
            {showGenericActions && canDelete && (
              <HeroAction
                icon={Trash2}
                label={t(locale, "deleteInitiative")}
                onClick={onDelete}
                danger
              />
            )}
          </div>
        </div>

        <div className="space-y-3">
          <span className="inline-flex items-center gap-2 px-3 h-7 rounded-full bg-white/15 font-sans text-[10px] tracking-[0.25em] uppercase">
            <span className="w-1.5 h-1.5 rounded-full bg-white" />
            {isRTL ? "مبادرة · " : "INITIATIVE · "}
            {getInitiativeType(initiative.type, locale).toUpperCase()}
          </span>
          <h1 className="font-display font-bold text-3xl md:text-5xl tracking-tight leading-tight max-w-4xl">
            {title}
          </h1>
          {description && (
            <p className="text-[15px] md:text-base text-white/80 max-w-2xl leading-relaxed line-clamp-2">
              {description}
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-4 font-sans text-[10px] tracking-[0.25em] uppercase text-white/85">
          <StatusInline status={initiative.status} locale={locale} />
          <span aria-hidden className="w-1 h-1 rounded-full bg-white/50" />
          <span>
            {isRTL ? "البداية · " : "STARTS · "}
            {formatHeroDate(initiative.start_date, locale)}
          </span>
          {initiative.end_date && (
            <>
              <span aria-hidden className="w-1 h-1 rounded-full bg-white/50" />
              <span>
                {isRTL ? "النهاية · " : "ENDS · "}
                {formatHeroDate(initiative.end_date, locale)}
              </span>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

function StatusInline({
  status,
  locale,
}: {
  status: string;
  locale: "ar" | "en";
}) {
  const live =
    status === "active" || status === "published" ? "bg-success" : "bg-white/60";
  return (
    <span className="inline-flex items-center gap-2">
      <span className={cn("w-1.5 h-1.5 rounded-full", live)} />
      <span>{getInitiativeStatus(status, locale).toUpperCase()}</span>
    </span>
  );
}

function pillarGradient(type: string): string {
  switch (type) {
    case "walk_initiative":
      return "from-brand-blue to-blue-800";
    case "juthoor_initiative":
      return "from-green-700 to-green-900";
    default:
      return "from-primary to-blue-700";
  }
}

function formatHeroDate(date: string | null | undefined, locale: "ar" | "en") {
  if (!date) return "—";
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(date));
}

// ─── Overview KPI strip ──────────────────────────────────────────────────

function OverviewKpiStrip({
  initiative,
  isRTL,
  locale,
}: {
  initiative: Initiative;
  isRTL: boolean;
  locale: "ar" | "en";
}) {
  const accent =
    initiative.type === "walk_initiative"
      ? "bg-brand-blue/10 text-brand-blue"
      : initiative.type === "juthoor_initiative"
      ? "bg-brand-green/10 text-brand-green"
      : "bg-primary/10 text-primary";
  const cells = [
    {
      caption: isRTL ? "الميزانية" : "BUDGET",
      value: `${formatNumber(parseFloat(initiative.init_budget || initiative.budget || "0"), locale)} NC`,
      icon: Wallet,
    },
    {
      caption: isRTL ? "المتبقّي" : "REMAINING",
      value: `${formatNumber(parseFloat(initiative.budget || "0"), locale)} NC`,
      icon: TrendingUp,
    },
    {
      caption: isRTL ? "المشاركون" : "PARTICIPANTS",
      value: formatNumber(parseInt(initiative.participants || "0", 10), locale),
      icon: Users,
    },
    {
      caption: isRTL ? "المدّة" : "DURATION",
      value: durationLabel(initiative.start_date, initiative.end_date, isRTL),
      icon: Calendar,
    },
  ];

  return (
    <div className="rounded-2xl border border-border bg-white shadow-sm overflow-hidden">
      <div className="grid grid-cols-2 lg:grid-cols-4 divide-y lg:divide-y-0 lg:divide-x rtl:lg:divide-x-reverse divide-border">
        {cells.map((c) => (
          <div
            key={c.caption}
            className="p-5 md:p-6 flex flex-col gap-3 min-h-[112px]"
          >
            <div className="flex items-center justify-between">
              <span className="font-sans text-[10px] tracking-[0.25em] text-foreground-secondary uppercase">
                {c.caption}
              </span>
              <span
                className={`inline-flex items-center justify-center w-7 h-7 rounded-lg ${accent}`}
                aria-hidden
              >
                <c.icon className="w-3.5 h-3.5" strokeWidth={1.8} />
              </span>
            </div>
            <p className="font-display font-bold text-2xl md:text-3xl tracking-tight tabular-nums leading-none text-foreground">
              {c.value}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function durationLabel(
  start: string | null | undefined,
  end: string | null | undefined,
  isRTL: boolean,
) {
  if (!start) return "—";
  const startD = new Date(start);
  const endD = end ? new Date(end) : null;
  if (!endD) return isRTL ? "مفتوحة" : "Open";
  const days = Math.max(
    1,
    Math.round((endD.getTime() - startD.getTime()) / 86400000),
  );
  return isRTL ? `${days} يوم` : `${days} days`;
}

// ─── Back link ────────────────────────────────────────────────────────────

function BackLink({
  locale,
  variant = "default",
}: {
  locale: "ar" | "en";
  variant?: "default" | "hero";
}) {
  const isRTL = locale === "ar";
  const Icon = isRTL ? ArrowRight : ArrowLeft;
  return (
    <Link
      to="/dashboard/initiatives"
      className={cn(
        "inline-flex items-center gap-2 font-sans text-[10px] tracking-[0.25em] uppercase transition-colors",
        variant === "hero"
          ? "text-white/80 hover:text-white"
          : "text-foreground-secondary hover:text-primary",
      )}
    >
      <Icon className="w-3.5 h-3.5" strokeWidth={2} />
      <span>{t(locale, "backToInitiatives")}</span>
    </Link>
  );
}
